// Catalog data hooks (clients, projects, tasks, tags). Reused by the tracker, timesheet and reports.
import type {
  ArchivedFilter,
  Client,
  ClientInput,
  ClientPatch,
  Project,
  ProjectInput,
  ProjectListQuery,
  ProjectMember,
  ProjectMembersInput,
  ProjectPatch,
  TagInput,
  TagItem,
  TagPatch,
  Task,
  TaskInput,
  TaskPatch,
  TeamMember,
} from '@taim/shared';
import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from '@/lib/api';

export type CatalogListOptions = { archived?: ArchivedFilter; search?: string };
/** `clientId`: one client's tags, or 'none' for tags without a client; omitted = all tags. */
export type TagListOptions = CatalogListOptions & { clientId?: string };

/** Query keys. Invalidating a root (e.g. `catalogKeys.projects`) refreshes every list under it. */
export const catalogKeys = {
  clients: ['clients'] as const,
  clientList: (o: CatalogListOptions) => ['clients', 'list', o] as const,
  projects: ['projects'] as const,
  projectList: (o: ProjectListQuery) => ['projects', 'list', o] as const,
  project: (id: string) => ['projects', 'detail', id] as const,
  members: (id: string) => ['projects', 'members', id] as const,
  tags: ['tags'] as const,
  tagList: (o: TagListOptions) => ['tags', 'list', o] as const,
};

const listQuery = (o: CatalogListOptions & { clientId?: string; userId?: string }) => ({
  archived: o.archived ?? 'false',
  search: o.search?.trim() || undefined,
  clientId: o.clientId,
  userId: o.userId,
});

/**
 * The catalog changes rarely, and only through mutations that refresh it (see useCatalogMutation),
 * so its lists stay fresh for five minutes and don't refetch on every return to the tab.
 */
export const CATALOG_STALE_TIME = 5 * 60_000;
const catalogFreshness = { staleTime: CATALOG_STALE_TIME, refetchOnWindowFocus: false } as const;

/** Query definitions, shared by the hooks below, the app shell's warm-up and report filters. */
export const catalogQueries = {
  clients: (opts: CatalogListOptions = {}) =>
    queryOptions({
      queryKey: catalogKeys.clientList(opts),
      queryFn: () => api.get<Client[]>('/api/clients', listQuery(opts)),
      ...catalogFreshness,
    }),
  projects: (opts: ProjectListQuery = {}) =>
    queryOptions({
      queryKey: catalogKeys.projectList(opts),
      queryFn: () => api.get<Project[]>('/api/projects', listQuery(opts)),
      ...catalogFreshness,
    }),
  tags: (opts: TagListOptions = {}) =>
    queryOptions({
      queryKey: catalogKeys.tagList(opts),
      queryFn: () => api.get<TagItem[]>('/api/tags', listQuery(opts)),
      ...catalogFreshness,
    }),
};

export function useClients(opts: CatalogListOptions = {}) {
  return useQuery({ ...catalogQueries.clients(opts), placeholderData: keepPreviousData });
}

/** Projects visible to the caller, with tasks, ordered NO CLIENT first, then client and name. */
export function useProjects(opts: ProjectListQuery = {}, enabled = true) {
  return useQuery({ ...catalogQueries.projects(opts), placeholderData: keepPreviousData, enabled });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: catalogKeys.project(id),
    queryFn: () => api.get<Project>(`/api/projects/${id}`),
  });
}

export function useProjectMembers(id: string, enabled = true) {
  return useQuery({
    queryKey: catalogKeys.members(id),
    queryFn: () => api.get<ProjectMember[]>(`/api/projects/${id}/members`),
    enabled,
  });
}

export function useTags(opts: TagListOptions = {}, enabled = true) {
  return useQuery({ ...catalogQueries.tags(opts), placeholderData: keepPreviousData, enabled });
}

/**
 * Every tag, archived ones included, from the one cached list that all tag pickers share (per
 * client lists are filtered from it in memory, never fetched).
 */
export function useAllTags() {
  return useQuery(catalogQueries.tags({ archived: 'all' }));
}

/**
 * Loads (and keeps cached) every tag, project and client the viewer may see, so tag and project
 * pickers open with their lists. Used once, by the app shell; it never re-renders its caller.
 */
export function useWarmCatalog() {
  const quiet = { notifyOnChangeProps: [] };
  useQuery({ ...catalogQueries.tags({ archived: 'all' }), ...quiet });
  useQuery({ ...catalogQueries.projects({ archived: 'all' }), ...quiet });
  useQuery({ ...catalogQueries.clients({ archived: 'all' }), ...quiet });
}

/**
 * Tags belong to a client: an entry may only carry tags of its project's client. `clientOf` gives
 * the client whose tags a project's entries use (undefined while projects load); `tagsFor` keeps the
 * tags that may stay when an entry moves to another client's project.
 */
export function useTagScope() {
  const projects = useProjects({ archived: 'all' });
  const tags = useAllTags();
  const tagClient = useMemo(
    () => tags.data && new Map(tags.data.map((t) => [t.id, t.clientId])),
    [tags.data],
  );
  const clientOf = (projectId: string | null | undefined): string | null | undefined => {
    if (!projectId) return null;
    const project = projects.data?.find((p) => p.id === projectId);
    return project ? project.clientId : undefined;
  };
  const tagsFor = (tagIds: string[], clientId: string | null | undefined) => {
    if (clientId === undefined || !tagClient) return tagIds;
    return tagIds.filter((id) => tagClient.get(id) === clientId);
  };
  /** The project/task patch for a project change, with `tagIds` when some tags must go. */
  const projectPatch = (value: { projectId: string; taskId: string | null } | null, tagIds: string[]) => {
    const kept = tagsFor(tagIds, clientOf(value?.projectId));
    return {
      projectId: value?.projectId ?? null,
      taskId: value?.taskId ?? null,
      ...(kept.length !== tagIds.length ? { tagIds: kept } : {}),
    };
  };
  return { clientOf, tagsFor, projectPatch };
}

/** Active users for the project access picker (served by the team feature). */
export function useUsersForPicker(enabled: boolean) {
  return useQuery({
    queryKey: ['users', 'picker'],
    queryFn: () => api.get<TeamMember[]>('/api/users'),
    enabled,
    retry: false,
  });
}

/** Mutation that refreshes the given catalog roots when it succeeds. */
function useCatalogMutation<TVars, TResult>(
  fn: (vars: TVars) => Promise<TResult>,
  roots: ReadonlyArray<readonly string[]>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => Promise.all(roots.map((queryKey) => qc.invalidateQueries({ queryKey }))),
  });
}

// Client names are shown on projects, so client changes also refresh projects.
export const useCreateClient = () =>
  useCatalogMutation((input: ClientInput) => api.post<Client>('/api/clients', input), [catalogKeys.clients]);
export const useUpdateClient = () =>
  useCatalogMutation(
    ({ id, ...patch }: ClientPatch & { id: string }) => api.patch<Client>(`/api/clients/${id}`, patch),
    [catalogKeys.clients, catalogKeys.projects],
  );
export const useDeleteClient = () =>
  useCatalogMutation((id: string) => api.delete<void>(`/api/clients/${id}`), [catalogKeys.clients]);

export const useCreateProject = () =>
  useCatalogMutation(
    (input: ProjectInput) => api.post<Project>('/api/projects', input),
    [catalogKeys.projects],
  );
export const useUpdateProject = () =>
  useCatalogMutation(
    ({ id, ...patch }: ProjectPatch & { id: string }) => api.patch<Project>(`/api/projects/${id}`, patch),
    [catalogKeys.projects],
  );
export const useDeleteProject = () =>
  useCatalogMutation((id: string) => api.delete<void>(`/api/projects/${id}`), [catalogKeys.projects]);
export const useSetProjectMembers = () =>
  useCatalogMutation(
    ({ projectId, ...input }: ProjectMembersInput & { projectId: string }) =>
      api.put<ProjectMember[]>(`/api/projects/${projectId}/members`, input),
    [catalogKeys.projects],
  );

export const useCreateTask = () =>
  useCatalogMutation(
    ({ projectId, ...input }: TaskInput & { projectId: string }) =>
      api.post<Task>(`/api/projects/${projectId}/tasks`, input),
    [catalogKeys.projects],
  );
export const useUpdateTask = () =>
  useCatalogMutation(
    ({ id, ...patch }: TaskPatch & { id: string }) => api.patch<Task>(`/api/tasks/${id}`, patch),
    [catalogKeys.projects],
  );
export const useDeleteTask = () =>
  useCatalogMutation((id: string) => api.delete<void>(`/api/tasks/${id}`), [catalogKeys.projects]);

export const useCreateTag = () =>
  useCatalogMutation((input: TagInput) => api.post<TagItem>('/api/tags', input), [catalogKeys.tags]);
export const useUpdateTag = () =>
  useCatalogMutation(
    ({ id, ...patch }: TagPatch & { id: string }) => api.patch<TagItem>(`/api/tags/${id}`, patch),
    [catalogKeys.tags],
  );
export const useDeleteTag = () =>
  useCatalogMutation((id: string) => api.delete<void>(`/api/tags/${id}`), [catalogKeys.tags]);
